import { PORTFOLIO_SKIM_FIELD, type PortfolioSkimField } from '@d3/contract';

import { SEARCH_RESULT_TEXT } from '../config/portfolio.text';
import { clipSearchText } from '../lib/portfolio-format';
import { type PortfolioSearchHit, type PortfolioSearchItem } from '../model/portfolio-search';

export interface SearchResultItemProps {
  item: PortfolioSearchItem;
  searchText: string;
  onSelect?: (item: PortfolioSearchItem) => void;
}

/**
 * 필드별 표시: 이모지(구 Header.ui 그대로 — 메모 📋 · 영업현황 📊 · 연관 파트너 🤝, DD-56) · 라벨 ·
 * 보여 줄 개수(메모 모두 · 영업현황 · 연관 파트너는 첫 건)
 */
const HIT_DISPLAY: Record<
  Exclude<PortfolioSkimField, 'name'>,
  { emoji: string; label: (datetime: string) => string; max: number }
> = {
  [PORTFOLIO_SKIM_FIELD.MEMO]: { emoji: '📋', label: SEARCH_RESULT_TEXT.memo, max: Infinity },
  [PORTFOLIO_SKIM_FIELD.REPORT]: { emoji: '📊', label: SEARCH_RESULT_TEXT.report, max: 1 },
  [PORTFOLIO_SKIM_FIELD.PARTNER]: { emoji: '🤝', label: SEARCH_RESULT_TEXT.round, max: 1 },
};

/** 보여 줄 조각 — 필드 순서는 서버 순서 그대로, 필드마다 max 건까지 */
function visibleHits(hits: readonly PortfolioSearchHit[]) {
  const shown = new Map<string, number>();
  return hits.flatMap((hit) => {
    const display = hit.field === 'name' ? undefined : HIT_DISPLAY[hit.field];
    const count = shown.get(hit.field) ?? 0;
    if (!display || count >= display.max) return [];
    shown.set(hit.field, count + 1);
    return [{ hit, display }];
  });
}

/** 걸린 본문 조각 하나: 구분(이모지 + "메모 (날짜)") + 검색어만 본문색인 본문 */
function SearchHit({
  emoji,
  label,
  hit,
  searchText,
}: {
  emoji: string;
  label: string;
  hit: PortfolioSearchHit;
  searchText: string;
}) {
  const clip = clipSearchText(hit.content, searchText);
  return (
    <span data-slot="hit" className="flex flex-col">
      <span className="flex items-center gap-1 text-fg-default typo-label-sm">
        <span aria-hidden="true" data-slot="emoji">
          {emoji}
        </span>
        {label}
      </span>
      <span className="text-fg-muted typo-caption">
        {clip ? (
          <>
            {SEARCH_RESULT_TEXT.ellipsis}
            {clip.before}
            <mark className="bg-transparent text-fg-default">{clip.match}</mark>
            {clip.after}
            {SEARCH_RESULT_TEXT.ellipsis}
          </>
        ) : null}
      </span>
    </span>
  );
}

/**
 * 헤더 검색 결과 한 건 = 회사 하나: 회사명 + 그 회사에서 검색어가 걸린 본문 조각들(서버가 포트폴리오별로 묶어 준다).
 * 목록(ul/li)은 부모가 두고, 항목은 눌러 고를 수 있는 버튼이다.
 */
export function SearchResultItem({ item, searchText, onSelect }: SearchResultItemProps) {
  return (
    <button
      type="button"
      data-slot="search-result-item"
      onClick={() => onSelect?.(item)}
      className="flex w-full cursor-pointer flex-col items-start gap-1 border-b border-border-subtle p-2-5 text-left"
    >
      <span className="text-fg-default typo-body-medium">{item.name}</span>
      {visibleHits(item.hits).map(({ hit, display }, index) => (
        <SearchHit
          // 걸린 본문은 필드 · 순서로만 구분된다
          key={`${hit.field}-${index}`}
          emoji={display.emoji}
          label={display.label(hit.datetime)}
          hit={hit}
          searchText={searchText}
        />
      ))}
    </button>
  );
}
